import React, { FunctionComponent, MouseEvent, useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { RowToolbar } from './RowToolbar';
import { SelectableOption } from 'model';
import RowToolbarMobile from './RowToolbarMobile';

interface Props {
    className?: string;
    id: number;
    content: JSX.Element;
    name: string;
    duplicateOptions?: Array<SelectableOption<string>>;
    statusId: number;
    selected: boolean;
    duplicated?: boolean;
    onEdit?: (id: number) => void;
    onActivate?: (id: number, active: boolean) => void;
    onDuplicate?: (id: number, name: string, selectedOptionsIds: Array<string>) => void;
    onDelete?: (id: number) => void;
    showToolbar?: boolean;
    onOpenToolbar?: () => void;
    mobileToolbar?: boolean;
    isMobile?: boolean;
}

interface StyleProps {
    mobileToolbar?: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>(theme =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            position: 'relative',
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            borderBottomColor: '#F1F1F1',
            height: 80
        },
        rowSelectionContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            width: 0
        },
        contentToolbarButtonContainer: props => ({
            display: 'flex',
            cursor: 'pointer',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                borderRadius: 10,
                minHeight: 60
            },
            [theme.breakpoints.only('md')]: {
                borderRadius: 14,
                minHeight: 60
            },
            [theme.breakpoints.only('lg')]: {
                borderRadius: 18,
                minHeight: 82
            },
            [theme.breakpoints.up('xl')]: {
                borderRadius: 22,
                minHeight: 82
            },
            [theme.breakpoints.down('md')]: {
                ...(props.mobileToolbar
                    ? {
                          '& > .toolbarButtonContainer': {
                              display: 'none'
                          }
                      }
                    : {})
            }
        }),

        selected: {
            background: '#F8F8F8'
        },
        contentContainer: {
            display: 'flex',
            width: '100%'
        },
        toolbarButtonContainer: props => ({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            [theme.breakpoints.down('sm')]: {
                width: 37
            },
            [theme.breakpoints.only('md')]: {
                width: 50
            },
            [theme.breakpoints.only('lg')]: {
                width: 67
            },
            [theme.breakpoints.up('xl')]: {
                width: 90
            },
            [theme.breakpoints.down('md')]: {
                ...(props.mobileToolbar
                    ? {
                          display: 'none'
                      }
                    : {})
            }
        }),
        toolbarContainer: props => ({
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
                width: 40,
                paddingLeft: 16
            },
            [theme.breakpoints.only('md')]: {
                width: 52,
                paddingLeft: 16
            },
            [theme.breakpoints.only('lg')]: {
                width: 70,
                paddingLeft: 16
            },
            [theme.breakpoints.up('xl')]: {
                width: 93,
                paddingLeft: 22
            },
            [theme.breakpoints.down('md')]: {
                ...(props.mobileToolbar
                    ? {
                          display: 'none'
                      }
                    : {})
            }
        }),
        toolbarMobileContainer: {
            [theme.breakpoints.down('md')]: {
                position: 'absolute',
                top: 0,
                right: 0,
                height: '100%',
                width: 45,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },
        toolbarMobileContainerNew: {},
        duplicated: {
            position: 'absolute',
            backgroundColor: '#5E8677',
            color: '#ffffff',
            [theme.breakpoints.down('lg')]: {
                height: 26,
                fontSize: 12,
                top: 60,
                right: 0
            },
            [theme.breakpoints.up('xl')]: {
                height: 30,
                fontSize: 15,
                top: 75,
                right: 0
            }
        },
        toolbarMobileButton: {
            'background': 'transparent',
            'border': 'none',
            'color': '#000',
            '& svg': {
                fontSize: 22,
                color: '#000'
            }
        }
    })
);

export const Row: FunctionComponent<Props> = props => {
    const classes = useStyles({ mobileToolbar: props.mobileToolbar });

    const [showToolbar, setShowToolbar] = useState(props.showToolbar || false);
    const [showDuplicated, setShowDuplicated] = useState(props.duplicated);
    const [showMobileDialog, setShowMobileDialog] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setShowDuplicated(false);
        }, 3000);
    }, []);

    useEffect(() => {
        showToolbar && props.onOpenToolbar && props.onOpenToolbar();
    }, [showToolbar]);

    useEffect(() => {
        setShowToolbar(props.showToolbar!);
    }, [props.showToolbar]);

    const clickToolbarButtonHandler = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        setShowToolbar(prevValue => !prevValue);
    };

    const clickToolbarMobileButtonHandler = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        setShowMobileDialog(true);
    };

    const onEditHandler = (): void => {
        setShowToolbar(false);
        props.onEdit && props.onEdit(props.id);
    };

    const onActivateHandler = (active: boolean): void => {
        setShowToolbar(false);
        props.onActivate && props.onActivate(props.id, active);
    };

    const onDuplicateHandler = (name: string, selectedOptionIds: Array<string>): void => {
        setShowToolbar(false);
        props.onDuplicate && props.onDuplicate(props.id, name, selectedOptionIds);
    };

    const onDeleteHandler = (): void => {
        setShowToolbar(false);
        props.onDelete && props.onDelete(props.id);
    };

    const closeMobileDialogHandler = (): void => {
        setShowMobileDialog(false);
    };

    const containerStyle = clsx(classes.container, props.className);
    const contentToolbarButtonContainerStyle = clsx(classes.contentToolbarButtonContainer, {
        [classes.selected]: props.selected
    });

    const toolbar = (
        <RowToolbar
            statusId={props.statusId}
            onEdit={onEditHandler}
            onActivate={props.onActivate ? onActivateHandler : undefined}
            onDuplicate={props.onDuplicate ? onDuplicateHandler : undefined}
            onDelete={props.onDelete ? onDeleteHandler : undefined}
            name={props.name}
            duplicateOptions={props.duplicateOptions}
        />
    );

    const toolbarMobile = (
        <RowToolbarMobile
            statusId={props.statusId}
            onEdit={onEditHandler}
            onActivate={props.onActivate ? onActivateHandler : undefined}
            onDuplicate={props.onDuplicate ? onDuplicateHandler : undefined}
            onDelete={props.onDelete ? onDeleteHandler : undefined}
            name={props.name}
            duplicateOptions={props.duplicateOptions}
            showDialog={showMobileDialog}
            onCloseDialog={closeMobileDialogHandler}
        />
    );

    const duplicated = showDuplicated && (
        <Chip
            className={classes.duplicated}
            label="Duplicated"
        />
    );

    return (
        <Box className={containerStyle}>
            <Box className={classes.rowSelectionContainer} />

            <Box
                className={contentToolbarButtonContainerStyle}
                onClick={onEditHandler}
            >
                <Box className={classes.contentContainer}>{props.content}</Box>
                <Box
                    className={classes.toolbarButtonContainer}
                    onClick={clickToolbarButtonHandler}
                    id="services_list_button_toolbar"
                >
                    {toolbar}
                </Box>

                {props.mobileToolbar && props.isMobile && (
                    <Box className={classes.toolbarMobileContainer}>
                        <button
                            type="button"
                            aria-label="Service actions"
                            onClick={clickToolbarMobileButtonHandler}
                            className={classes.toolbarMobileButton}
                        >
                            <FontAwesomeIcon icon={faEllipsis} />
                        </button>
                    </Box>
                )}
            </Box>

            {props.mobileToolbar && props.isMobile ? (
                <Box className={classes.toolbarMobileContainerNew}>{toolbarMobile}</Box>
            ) : undefined}

            {duplicated}
        </Box>
    );
};

export default Row;
